
import React from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { usePublicProfile } from "@/hooks/usePublicProfile";
import { useUserProfile } from "@/hooks/useUserProfile";
import Header from "@/components/Header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Helmet } from "react-helmet-async";
import { Loader2, User } from "lucide-react";
import { format } from "date-fns";

const ProfilePage = () => {
  const { username } = useParams<{ username: string }>();
  const { user } = useAuth();
  const { username: currentUsername } = useUserProfile();
  
  // If no username in URL, show current user's profile
  const isOwnProfile = !username || username === currentUsername;
  const targetUsername = username || currentUsername;
  
  const { profile, isLoading, error } = usePublicProfile(isOwnProfile ? undefined : targetUsername);
  
  // For own profile, we can use the auth user data
  const displayProfile = isOwnProfile ? {
    id: user?.id,
    username: currentUsername,
    profile_picture: null, // Will be handled by useUserProfile
    avatar_url: user?.user_metadata?.avatar_url,
    is_private: false,
    created_at: user?.created_at
  } : profile;

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Header pageName="Profile" useBackButton={true} />
        <div className="container max-w-4xl py-4 pt-[64px] flex items-center justify-center min-h-[50vh]">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      </div>
    );
  }

  if (error || !displayProfile) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Helmet>
          <title>Profile Not Found - 3DP Hub</title>
        </Helmet>
        <Header pageName="Profile" useBackButton={true} />
        <div className="container max-w-4xl py-4 pt-[64px]">
          <Card>
            <CardContent className="p-8 text-center">
              <User className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
              <h2 className="text-xl font-semibold mb-2">
                {error === 'User not found' ? 'User Not Found' : 'Profile Unavailable'}
              </h2>
              <p className="text-muted-foreground">
                {error === 'User not found' 
                  ? 'The user you are looking for does not exist.' 
                  : 'This profile could not be loaded.'}
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Check if profile is private and not own profile
  if (!isOwnProfile && displayProfile.is_private) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Helmet>
          <title>{displayProfile.username} - 3DP Hub</title>
        </Helmet>
        <Header pageName="Profile" useBackButton={true} />
        <div className="container max-w-4xl py-4 pt-[64px]">
          <Card>
            <CardContent className="p-8 text-center">
              <User className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
              <h2 className="text-xl font-semibold mb-2">Private Profile</h2>
              <p className="text-muted-foreground">
                This user has set their profile to private.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const profilePicture = displayProfile.profile_picture || displayProfile.avatar_url;
  const joinDate = displayProfile.created_at ? format(new Date(displayProfile.created_at), 'MMMM yyyy') : null;

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Helmet>
        <title>{displayProfile.username ? `${displayProfile.username} - 3DP Hub` : 'Profile - 3DP Hub'}</title>
        <meta name="description" content={`${displayProfile.username}'s profile on 3DP Hub`} />
      </Helmet>
      
      <Header 
        pageName={isOwnProfile ? "My Profile" : `${displayProfile.username}'s Profile`}
        useBackButton={true}
      />
      
      <div className="container max-w-4xl py-4 pt-[64px]">
        <Card>
          <CardHeader className="pb-4">
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <Avatar className="h-20 w-20">
                <AvatarImage src={profilePicture || undefined} />
                <AvatarFallback className="text-lg">
                  <User className="h-8 w-8" />
                </AvatarFallback>
              </Avatar>
              
              <div className="text-center sm:text-left">
                <CardTitle className="text-2xl">
                  {displayProfile.username || 'Anonymous User'}
                </CardTitle>
                <div className="flex flex-wrap gap-2 mt-2 justify-center sm:justify-start">
                  {isOwnProfile && (
                    <Badge variant="secondary">Your Profile</Badge>
                  )}
                  {displayProfile.is_private && (
                    <Badge variant="outline">Private</Badge>
                  )}
                  {joinDate && (
                    <Badge variant="outline">Joined {joinDate}</Badge>
                  )}
                </div>
              </div>
            </div>
          </CardHeader>
          
          <CardContent>
            <div className="space-y-6">
              {/* Profile Stats Section - Placeholder for future expansion */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                  <CardContent className="p-4 text-center">
                    <div className="text-2xl font-bold">0</div>
                    <div className="text-sm text-muted-foreground">Tutorials Created</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <div className="text-2xl font-bold">0</div>
                    <div className="text-sm text-muted-foreground">Parts Contributed</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <div className="text-2xl font-bold">0</div>
                    <div className="text-sm text-muted-foreground">Reputation</div>
                  </CardContent>
                </Card>
              </div>
              
              {isOwnProfile && (
                <div className="text-center pt-4 border-t">
                  <p className="text-sm text-muted-foreground">
                    This is how your profile appears to other users. 
                    Visit <a href="/preferences" className="text-primary hover:underline">Preferences</a> to update your settings.
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ProfilePage;
